const STORAGE_VERSION = 'v1';
const MAGIC_LINK_REDIRECT_PATH_KEY = `natter:auth:magicLinkRedirectPath:${STORAGE_VERSION}`;
const MAGIC_LINK_PREVIOUS_PATH_KEY = `natter:auth:magicLinkPreviousPath:${STORAGE_VERSION}`;
const MAGIC_LINK_START_TIME_KEY = `natter:auth:magicLinkStartTime:${STORAGE_VERSION}`;
const MAGIC_LINK_COOLDOWN_KEY = `natter:auth:magicLinkCooldown:${STORAGE_VERSION}`;
// OTP resend gets its OWN slot: the magic-link and OTP resend cooldowns are independent flows, and a
// single shared slot would let a write/clear in one clobber a pending cooldown in the other.
const OTP_COOLDOWN_KEY = `natter:auth:otpCooldown:${STORAGE_VERSION}`;

/**
 * Stores the redirect path in sessionStorage for TellUsAboutYourself page.
 * Used to persist the redirect path across page refresh so the extended form is shown.
 */
export const setMagicLinkRedirectPath = (path: string): void => {
  sessionStorage.setItem(MAGIC_LINK_REDIRECT_PATH_KEY, path);
};

/**
 * Retrieves the stored redirect path from sessionStorage.
 */
export const getMagicLinkRedirectPath = (): string | null => sessionStorage.getItem(MAGIC_LINK_REDIRECT_PATH_KEY);

/**
 * Clears the redirect path from sessionStorage.
 * Should be called after successful profile submission.
 */
export const clearMagicLinkRedirectPath = (): void => {
  sessionStorage.removeItem(MAGIC_LINK_REDIRECT_PATH_KEY);
};

/**
 * Stores the previous path in sessionStorage for magic link flow.
 * Used to redirect users back to where they came from after authentication.
 */
export const setMagicLinkPreviousPath = (path: string): void => {
  sessionStorage.setItem(MAGIC_LINK_PREVIOUS_PATH_KEY, path);
};

/**
 * Retrieves the stored previous path from sessionStorage.
 */
export const getMagicLinkPreviousPath = (): string | null => sessionStorage.getItem(MAGIC_LINK_PREVIOUS_PATH_KEY);

/**
 * Clears the previous path from sessionStorage.
 */
export const clearMagicLinkPreviousPath = (): void => {
  sessionStorage.removeItem(MAGIC_LINK_PREVIOUS_PATH_KEY);
};

/**
 * Window within which a stored start time may still attribute a login to the magic-link flow.
 * Matches the 1h Keycloak action-token TTL: a link cannot succeed after it expires, so a marker
 * older than this is a stale/abandoned flow and must NOT attribute a later SSO/password/OTP login.
 * NOTE: resend does NOT reset this marker — time-to-login is measured from first intent; a click
 * more than 1h after the FIRST submit is intentionally not attributed (accepted under-count).
 */
const MAGIC_LINK_ATTRIBUTION_WINDOW_MS = 60 * 60 * 1000;

/**
 * Stores the start time of the magic link flow in localStorage (NOT sessionStorage).
 * localStorage is shared across tabs on the same origin, so the marker survives the emailed
 * link opening in a new tab — the case sessionStorage dropped, causing login_success to fire
 * for only ~6% of real logins. Best-effort: never throws (private mode / quota).
 */
export const setMagicLinkStartTime = (): void => {
  try {
    localStorage.setItem(MAGIC_LINK_START_TIME_KEY, Date.now().toString());
  } catch {
    /* storage unavailable — telemetry is best-effort */
  }
};

/**
 * Retrieves the stored magic link start time, or null if absent, stale (beyond the attribution
 * window), or corrupt. Self-heals by clearing stale/corrupt values so they cannot mis-attribute
 * a later non-magic-link login.
 */
export const getMagicLinkStartTime = (): number | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(MAGIC_LINK_START_TIME_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  const startTime = parseInt(raw, 10);
  if (Number.isNaN(startTime) || Date.now() - startTime > MAGIC_LINK_ATTRIBUTION_WINDOW_MS) {
    clearMagicLinkStartTime();
    return null;
  }
  return startTime;
};

/**
 * Clears the magic link start time from localStorage.
 * Called after successful authentication and at every non-magic-link auth entry point (SSO/password/OTP).
 */
export const clearMagicLinkStartTime = (): void => {
  try {
    localStorage.removeItem(MAGIC_LINK_START_TIME_KEY);
  } catch {
    /* ignore */
  }
};

const LOGIN_SUCCESS_SESSION_KEY = `natter:auth:loginSuccessSession:${STORAGE_VERSION}`;

/**
 * Records the Keycloak `session_state` for which `login_success` has already been emitted.
 *
 * A cross-device magic-link login (link opened on a different device/browser than the one that made
 * the request) has NO start-time marker on the completing device, so it is attributed via the
 * device-independent `login_method=ext-magic-link` token claim instead. Because that claim persists
 * for the whole Keycloak session, every later silent `check-sso` token refresh would re-fire the
 * event; this key guards the cross-device path so the login is counted ONCE per session. Best-effort
 * — never throws (private mode / quota).
 */
export const getLoginSuccessSession = (): string | null => {
  try {
    return localStorage.getItem(LOGIN_SUCCESS_SESSION_KEY);
  } catch {
    return null;
  }
};

export const setLoginSuccessSession = (sessionState: string): void => {
  try {
    localStorage.setItem(LOGIN_SUCCESS_SESSION_KEY, sessionState);
  } catch {
    /* storage unavailable — telemetry is best-effort */
  }
};

/**
 * Stores the cooldown end timestamp and associated email in sessionStorage.
 * Used to persist the resend cooldown timer across back-navigation.
 */
export const setMagicLinkCooldown = (email: string, endTime: number): void => {
  sessionStorage.setItem(MAGIC_LINK_COOLDOWN_KEY, JSON.stringify({ email, endTime }));
};

/**
 * Retrieves the remaining cooldown seconds for the given email.
 * @returns Remaining seconds if cooldown is active for this email, null otherwise.
 */
export const getMagicLinkCooldownRemaining = (email: string): number | null => {
  const raw = sessionStorage.getItem(MAGIC_LINK_COOLDOWN_KEY);
  if (!raw) return null;

  try {
    const { email: storedEmail, endTime } = JSON.parse(raw);
    if (storedEmail !== email) return null;

    const remaining = Math.ceil((endTime - Date.now()) / 1000);
    return remaining > 0 ? remaining : null;
  } catch {
    return null;
  }
};

/**
 * Clears the magic link cooldown data from sessionStorage.
 */
export const clearMagicLinkCooldown = (): void => {
  sessionStorage.removeItem(MAGIC_LINK_COOLDOWN_KEY);
};

/**
 * Stores the OTP resend cooldown end timestamp, keyed off the OTP `cid` (the resend rotates the cid,
 * so the cooldown must be findable at the cid that lands in the URL). Uses a dedicated slot so it is
 * independent of the magic-link cooldown.
 */
export const setOtpCooldown = (key: string, endTime: number): void => {
  sessionStorage.setItem(OTP_COOLDOWN_KEY, JSON.stringify({ key, endTime }));
};

/**
 * Retrieves the remaining OTP resend cooldown seconds for the given key (cid).
 * @returns Remaining seconds if a cooldown is active for this key, null otherwise.
 */
export const getOtpCooldownRemaining = (key: string): number | null => {
  const raw = sessionStorage.getItem(OTP_COOLDOWN_KEY);
  if (!raw) return null;

  try {
    const { key: storedKey, endTime } = JSON.parse(raw);
    if (storedKey !== key) return null;

    const remaining = Math.ceil((endTime - Date.now()) / 1000);
    return remaining > 0 ? remaining : null;
  } catch {
    return null;
  }
};

/**
 * Clears the OTP resend cooldown data from sessionStorage.
 */
export const clearOtpCooldown = (): void => {
  sessionStorage.removeItem(OTP_COOLDOWN_KEY);
};
